import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { AppNav, AppStyles } from './common';
import { renderMarkdown } from './markdown';

import { appAnalyticsAPI } from '../../services/api';

// Source of truth: docs/app-integration.md (repo root), served to logged-in
// customers by the API — deliberately not a public static file.

const AppIntegrationDocsPage: React.FC = () => {
  const [md, setMd] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    appAnalyticsAPI.getDocs()
      .then(r => setMd(r.data?.data?.markdown || ''))
      .catch(e => setError(`Could not load the documentation (${e?.response?.status || e.message}).`));
  }, []);

  const { html, toc } = useMemo(() => renderMarkdown(md), [md]);

  useEffect(() => {
    if (!html) return;
    if (window.location.hash) document.getElementById(window.location.hash.slice(1))?.scrollIntoView();
    const handler = (e: Event) => {
      const t = e.target as HTMLElement;
      if (t.classList.contains('ca-copy')) {
        const code = t.closest('.ca-code')?.querySelector('code')?.textContent || '';
        navigator.clipboard?.writeText(code).then(() => { t.textContent = 'Copied'; setTimeout(() => { t.textContent = 'Copy'; }, 1500); }).catch(() => {});
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [html]);

  const content = (
    <>
      <AppStyles />
      <style>{`.ca-doc h1{font-size:1.8rem}.ca-doc h2{font-size:1.35rem;border-bottom:1px solid #eef2f7;padding-bottom:.3rem}.ca-doc h3{font-size:1.1rem}.ca-doc p,.ca-doc li{line-height:1.65}.ca-doc table code,.ca-doc p code,.ca-doc li code{background:#f1f5f9;padding:1px 4px;border-radius:4px;color:#be185d}`}</style>
      <div className="container-fluid py-4">
        <AppNav />
        <div className="row g-4">
          <div className="col-lg-3 d-none d-lg-block">
            <div className="position-sticky" style={{ top: 80 }}>
              <div className="small fw-bold text-uppercase text-muted mb-2">On this page</div>
              <nav className="small">
                {toc.map(t => (
                  <a key={t.id} href={`#${t.id}`} className={`d-block text-decoration-none py-1 ${t.level === 3 ? 'ps-3 text-muted' : 'text-dark'}`}
                    onClick={e => { e.preventDefault(); document.getElementById(t.id)?.scrollIntoView({ behavior: 'smooth' }); window.history.replaceState(null, '', `#${t.id}`); }}>
                    {t.text}
                  </a>
                ))}
              </nav>
              <Link to="/app-analytics/setup" className="btn btn-primary btn-sm mt-3 w-100"><i className="bi bi-magic me-1"></i>Open the setup wizard</Link>
            </div>
          </div>
          <div className="col-lg-9">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-4 ca-doc">
                {error && <div className="alert alert-danger">{error}</div>}
                {!md && !error && <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>}
                {/* eslint-disable-next-line react/no-danger */}
                <div dangerouslySetInnerHTML={{ __html: html }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return <DashboardLayout>{content}</DashboardLayout>;
};

export default AppIntegrationDocsPage;
